import React, { useState, useEffect } from 'react';
import api from '../services/api';
import Swal from 'sweetalert2';
import { obtenerFechaLocal, formatearFechaLegible } from '../utils/dateUtils';

const GestionSubcontratistas = ({ proyectoId, proyecto }) => {
  const [subcontratistas, setSubcontratistas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vistaActual, setVistaActual] = useState('lista'); // 'lista', 'pagos'
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [subcontratistaEditando, setSubcontratistaEditando] = useState(null);
  const [mostrarFormularioPago, setMostrarFormularioPago] = useState(false);
  const [subcontratistaParaPago, setSubcontratistaParaPago] = useState(null);

  const [formulario, setFormulario] = useState({
    nombre: '',
    tipo_trabajo: '',
    monto_contrato: '',
    modalidad: 'por_ajuste',
    descripcion_trabajo: ''
  });

  const [formularioPago, setFormularioPago] = useState({
    concepto: '',
    porcentaje: '',
    monto: '',
    metodo_pago: 'transferencia',
    comprobante: ''
  });

  const tiposTrabajo = [
    { valor: 'albanileria', label: '🧱 Albañilería' },
    { valor: 'electricidad', label: '⚡ Electricidad' },
    { valor: 'plomeria', label: '🚰 Plomería' },
    { valor: 'pintura', label: '🎨 Pintura' },
    { valor: 'carpinteria', label: '🪚 Carpintería' },
    { valor: 'herreria', label: '🔨 Herrería' },
    { valor: 'limpieza', label: '🧹 Limpieza' },
    { valor: 'excavacion', label: '🚜 Excavación' },
    { valor: 'otro', label: '📋 Otro' }
  ];

  useEffect(() => {
    cargarSubcontratistas();
  }, [proyectoId]);

  const cargarSubcontratistas = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/proyectos/${proyectoId}/subcontratistas`);
      setSubcontratistas(response.data);
    } catch (error) {
      console.error('Error al cargar subcontratistas:', error);
      Swal.fire('Error', 'No se pudieron cargar los subcontratistas', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormulario(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleChangePago = (e) => {
    const { name, value } = e.target;
    setFormularioPago(prev => ({
      ...prev,
      [name]: value
    }));

    // Si se ingresa porcentaje, calcular monto automáticamente
    if (name === 'porcentaje' && subcontratistaParaPago) {
      const monto = (parseFloat(value) / 100) * parseFloat(subcontratistaParaPago.monto_contrato || 0);
      setFormularioPago(prev => ({
        ...prev,
        monto: monto.toFixed(2)
      }));
    }

    // Si se ingresa monto, calcular porcentaje automáticamente
    if (name === 'monto' && subcontratistaParaPago) {
      const porcentaje = (parseFloat(value) / parseFloat(subcontratistaParaPago.monto_contrato || 1)) * 100;
      setFormularioPago(prev => ({
        ...prev,
        porcentaje: porcentaje.toFixed(2)
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formulario.nombre || !formulario.tipo_trabajo || !formulario.monto_contrato) {
      Swal.fire('Error', 'Por favor completa los campos obligatorios', 'warning');
      return;
    }

    try {
      if (subcontratistaEditando) {
        await api.put(`/subcontratistas/${subcontratistaEditando.id}`, formulario);
        Swal.fire('✅ Actualizado', 'Subcontratista actualizado correctamente', 'success');
      } else {
        await api.post(`/proyectos/${proyectoId}/subcontratistas`, formulario);
        Swal.fire('✅ Registrado', 'Subcontratista registrado correctamente', 'success');
      }

      limpiarFormulario();
      cargarSubcontratistas();
      setMostrarFormulario(false);
    } catch (error) {
      console.error('Error al guardar:', error);
      Swal.fire('Error', error.response?.data?.mensaje || 'No se pudo guardar', 'error');
    }
  };

  const handleSubmitPago = async (e) => {
    e.preventDefault();

    if (!formularioPago.concepto || !formularioPago.monto) {
      Swal.fire('Error', 'Por favor completa los campos obligatorios', 'warning');
      return;
    }

    if (parseFloat(formularioPago.monto) > parseFloat(subcontratistaParaPago.saldo_pendiente)) {
      Swal.fire('Error', 'El monto excede el saldo pendiente', 'warning');
      return;
    }

    try {
      await api.post(`/proyectos/${proyectoId}/subcontratistas/pagos`, {
        SubcontratistaId: subcontratistaParaPago.id,
        ...formularioPago
      });

      Swal.fire('✅ Registrado', 'Pago registrado correctamente', 'success');
      limpiarFormularioPago();
      cargarSubcontratistas();
      setMostrarFormularioPago(false);
    } catch (error) {
      console.error('Error al registrar pago:', error);
      Swal.fire('Error', error.response?.data?.mensaje || 'No se pudo registrar el pago', 'error');
    }
  };

  const editarSubcontratista = (subcontratista) => {
    setSubcontratistaEditando(subcontratista);
    setFormulario({
      nombre: subcontratista.nombre,
      tipo_trabajo: subcontratista.tipo_trabajo,
      monto_contrato: subcontratista.monto_contrato,
      modalidad: subcontratista.modalidad,
      descripcion_trabajo: subcontratista.descripcion_trabajo || ''
    });
    setMostrarFormulario(true);
  };

  const cambiarEstado = async (subcontratista) => {
    const nuevoEstado = subcontratista.estado === 'activo' ? 'completado' : 'activo';
    
    const result = await Swal.fire({
      title: `¿Cambiar estado a ${nuevoEstado}?`,
      text: `${subcontratista.nombre}`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3b82f6',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Sí, cambiar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        await api.put(`/subcontratistas/${subcontratista.id}`, { estado: nuevoEstado });
        Swal.fire('✅ Actualizado', 'Estado actualizado correctamente', 'success');
        cargarSubcontratistas();
      } catch (error) {
        console.error('Error al cambiar estado:', error);
        Swal.fire('Error', 'No se pudo actualizar el estado', 'error');
      }
    }
  };

  const abrirFormularioPago = (subcontratista) => {
    if (subcontratista.saldo_pendiente <= 0) {
      Swal.fire('Info', 'Este subcontratista ya ha sido pagado completamente', 'info');
      return;
    }
    setSubcontratistaParaPago(subcontratista);
    setFormularioPago({
      concepto: '',
      porcentaje: '',
      monto: '',
      metodo_pago: 'transferencia',
      comprobante: ''
    });
    setMostrarFormularioPago(true);
  };

  const limpiarFormulario = () => {
    setFormulario({
      nombre: '',
      tipo_trabajo: '',
      monto_contrato: '',
      modalidad: 'por_ajuste',
      descripcion_trabajo: ''
    });
    setSubcontratistaEditando(null);
  };

  const limpiarFormularioPago = () => {
    setFormularioPago({
      concepto: '',
      porcentaje: '',
      monto: '',
      metodo_pago: 'transferencia',
      comprobante: ''
    });
    setSubcontratistaParaPago(null);
  };

  const formatMoney = (valor) => {
    return `RD$ ${parseFloat(valor || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getTipoTrabajoLabel = (tipo) => {
    const tipoObj = tiposTrabajo.find(t => t.valor === tipo);
    return tipoObj ? tipoObj.label : tipo;
  };

  const calcularTotales = () => {
    const totalContratos = subcontratistas.reduce((sum, s) => sum + parseFloat(s.monto_contrato || 0), 0);
    const totalPagado = subcontratistas.reduce((sum, s) => sum + parseFloat(s.total_pagado || 0), 0);
    const totalPendiente = subcontratistas.reduce((sum, s) => sum + parseFloat(s.saldo_pendiente || 0), 0);
    
    return { totalContratos, totalPagado, totalPendiente };
  };

  const totales = calcularTotales();
  const subcontratistasActivos = subcontratistas.filter(s => s.estado === 'activo');
  const subcontratistasCompletados = subcontratistas.filter(s => s.estado === 'completado');

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>🔧 Gestión de Subcontratistas</h2>
          <p style={styles.subtitle}>{proyecto?.nombre}</p>
        </div>
        <button
          onClick={() => {
            limpiarFormulario();
            setMostrarFormulario(!mostrarFormulario);
          }}
          style={styles.btnPrimary}
        >
          {mostrarFormulario ? '✕ Cancelar' : '+ Registrar Subcontratista'}
        </button>
      </div>

      {/* Resumen */}
      <div style={styles.resumen}>
        <div style={styles.resumenCard}>
          <div style={styles.resumenIcon}>💰</div>
          <div>
            <p style={styles.resumenLabel}>Total Contratos</p>
            <h3 style={styles.resumenValue}>{formatMoney(totales.totalContratos)}</h3>
          </div>
        </div>
        <div style={styles.resumenCard}>
          <div style={styles.resumenIcon}>✅</div>
          <div>
            <p style={styles.resumenLabel}>Total Pagado</p>
            <h3 style={styles.resumenValue}>{formatMoney(totales.totalPagado)}</h3>
          </div>
        </div>
        <div style={styles.resumenCard}>
          <div style={styles.resumenIcon}>⏳</div>
          <div>
            <p style={styles.resumenLabel}>Pendiente</p>
            <h3 style={{...styles.resumenValue, color: '#f59e0b'}}>
              {formatMoney(totales.totalPendiente)}
            </h3>
          </div>
        </div>
        <div style={styles.resumenCard}>
          <div style={styles.resumenIcon}>👷</div>
          <div>
            <p style={styles.resumenLabel}>Activos</p>
            <h3 style={styles.resumenValue}>{subcontratistasActivos.length}</h3>
          </div>
        </div>
      </div>

      {/* Formulario de Subcontratista */}
      {mostrarFormulario && (
        <div style={styles.formulario}>
          <h3 style={styles.formTitle}>
            {subcontratistaEditando ? '✏️ Editar Subcontratista' : '➕ Nuevo Subcontratista'}
          </h3>

          <form onSubmit={handleSubmit}>
            <div style={styles.formGrid}>
              {/* Nombre */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Nombre / Empresa *</label>
                <input
                  type="text"
                  name="nombre"
                  value={formulario.nombre}
                  onChange={handleChange}
                  placeholder="Nombre del subcontratista"
                  style={styles.input}
                  required
                />
              </div>

              {/* Tipo de Trabajo */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Tipo de Trabajo *</label>
                <select
                  name="tipo_trabajo"
                  value={formulario.tipo_trabajo}
                  onChange={handleChange}
                  style={styles.select}
                  required
                >
                  <option value="">Selecciona...</option>
                  {tiposTrabajo.map(tipo => (
                    <option key={tipo.valor} value={tipo.valor}>
                      {tipo.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Monto Contrato */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Monto del Contrato (RD$) *</label>
                <input
                  type="number"
                  name="monto_contrato"
                  value={formulario.monto_contrato}
                  onChange={handleChange}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  style={styles.input}
                  required
                />
              </div>

              {/* Modalidad */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Modalidad *</label>
                <select
                  name="modalidad"
                  value={formulario.modalidad}
                  onChange={handleChange}
                  style={styles.select}
                  required
                >
                  <option value="por_ajuste">Por Ajuste</option>
                  <option value="por_avance">Por Avance</option>
                </select>
              </div>

              {/* Descripción */}
              <div style={{...styles.formGroup, gridColumn: '1 / -1'}}>
                <label style={styles.label}>Descripción del Trabajo</label>
                <textarea
                  name="descripcion_trabajo"
                  value={formulario.descripcion_trabajo}
                  onChange={handleChange}
                  placeholder="Detalles del trabajo a realizar..."
                  style={{...styles.input, minHeight: '80px', resize: 'vertical'}}
                />
              </div>
            </div>

            {/* Botones */}
            <div style={styles.formActions}>
              <button
                type="button"
                onClick={() => {
                  limpiarFormulario();
                  setMostrarFormulario(false);
                }}
                style={styles.btnSecondary}
              >
                Cancelar
              </button>
              <button type="submit" style={styles.btnSubmit}>
                {subcontratistaEditando ? '💾 Actualizar' : '💾 Guardar'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Formulario de Pago */}
      {mostrarFormularioPago && subcontratistaParaPago && (
        <div style={styles.modalOverlay}>
          <div style={styles.formularioPago}>
            <h3 style={styles.formTitle}>💰 Registrar Pago</h3>
            <p style={styles.subcontratistaNombre}>{subcontratistaParaPago.nombre}</p>
            
            <div style={styles.infoPago}>
              <div style={styles.infoItem}>
                <span>Monto Contrato:</span>
                <strong>{formatMoney(subcontratistaParaPago.monto_contrato)}</strong>
              </div>
              <div style={styles.infoItem}>
                <span>Total Pagado:</span>
                <strong>{formatMoney(subcontratistaParaPago.total_pagado)}</strong>
              </div>
              <div style={styles.infoItem}>
                <span>Saldo Pendiente:</span>
                <strong style={{color: '#f59e0b'}}>
                  {formatMoney(subcontratistaParaPago.saldo_pendiente)}
                </strong>
              </div>
            </div>

            <form onSubmit={handleSubmitPago}>
              <div style={styles.formGrid}>
                {/* Concepto */}
                <div style={{...styles.formGroup, gridColumn: '1 / -1'}}>
                  <label style={styles.label}>Concepto del Pago *</label>
                  <input
                    type="text"
                    name="concepto"
                    value={formularioPago.concepto}
                    onChange={handleChangePago}
                    placeholder="Ej: Anticipo, Primer avance, Liquidación final..."
                    style={styles.input}
                    required
                  />
                </div>

                {/* Porcentaje */}
                <div style={styles.formGroup}>
                  <label style={styles.label}>Porcentaje (%)</label>
                  <input
                    type="number"
                    name="porcentaje"
                    value={formularioPago.porcentaje}
                    onChange={handleChangePago}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    max="100"
                    style={styles.input}
                  />
                </div>

                {/* Monto */}
                <div style={styles.formGroup}>
                  <label style={styles.label}>Monto (RD$) *</label>
                  <input
                    type="number"
                    name="monto"
                    value={formularioPago.monto}
                    onChange={handleChangePago}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    style={styles.input}
                    required
                  />
                </div>

                {/* Método de Pago */}
                <div style={styles.formGroup}>
                  <label style={styles.label}>Método de Pago</label>
                  <select
                    name="metodo_pago"
                    value={formularioPago.metodo_pago}
                    onChange={handleChangePago}
                    style={styles.select}
                  >
                    <option value="efectivo">Efectivo</option>
                    <option value="transferencia">Transferencia</option>
                    <option value="cheque">Cheque</option>
                  </select>
                </div>

                {/* Comprobante */}
                <div style={styles.formGroup}>
                  <label style={styles.label}>No. Comprobante</label>
                  <input
                    type="text"
                    name="comprobante"
                    value={formularioPago.comprobante}
                    onChange={handleChangePago}
                    placeholder="Número de referencia"
                    style={styles.input}
                  />
                </div>
              </div>

              {/* Botones */}
              <div style={styles.formActions}>
                <button
                  type="button"
                  onClick={() => {
                    limpiarFormularioPago();
                    setMostrarFormularioPago(false);
                  }}
                  style={styles.btnSecondary}
                >
                  Cancelar
                </button>
                <button type="submit" style={styles.btnSubmit}>
                  💰 Registrar Pago
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Lista de Subcontratistas */}
      {loading ? (
        <div style={styles.loading}>
          <div style={styles.spinner}></div>
          <p>Cargando subcontratistas...</p>
        </div>
      ) : subcontratistas.length === 0 ? (
        <div style={styles.empty}>
          <div style={styles.emptyIcon}>👷</div>
          <h3>No hay subcontratistas registrados</h3>
          <p>Comienza registrando el primer subcontratista del proyecto</p>
        </div>
      ) : (
        <>
          {/* Activos */}
          {subcontratistasActivos.length > 0 && (
            <>
              <h3 style={styles.sectionTitle}>✅ Activos ({subcontratistasActivos.length})</h3>
              <div style={styles.listaSubcontratistas}>
                {subcontratistasActivos.map((subcontratista) => (
                  <div key={subcontratista.id} style={styles.subcontratistaCard}>
                    <div style={styles.cardHeader}>
                      <div>
                        <h4 style={styles.nombre}>{subcontratista.nombre}</h4>
                        <p style={styles.tipo}>{getTipoTrabajoLabel(subcontratista.tipo_trabajo)}</p>
                        {subcontratista.descripcion_trabajo && (
                          <p style={styles.descripcion}>{subcontratista.descripcion_trabajo}</p>
                        )}
                      </div>
                      <div style={styles.monto}>{formatMoney(subcontratista.monto_contrato)}</div>
                    </div>

                    <div style={styles.cardInfo}>
                      <div style={styles.infoItem}>
                        <span style={styles.infoLabel}>💼 Modalidad:</span>
                        <span>{subcontratista.modalidad === 'por_ajuste' ? 'Por Ajuste' : 'Por Avance'}</span>
                      </div>
                      <div style={styles.infoItem}>
                        <span style={styles.infoLabel}>✅ Pagado:</span>
                        <span>{formatMoney(subcontratista.total_pagado)} ({subcontratista.porcentaje_pagado}%)</span>
                      </div>
                      <div style={styles.infoItem}>
                        <span style={styles.infoLabel}>⏳ Pendiente:</span>
                        <span style={{fontWeight: '600', color: '#f59e0b'}}>
                          {formatMoney(subcontratista.saldo_pendiente)}
                        </span>
                      </div>
                      <div style={styles.infoItem}>
                        <span style={styles.infoLabel}>📅 Contrato:</span>
                        <span>{formatearFechaLegible(subcontratista.fecha_contrato)}</span>
                      </div>
                    </div>

                    {/* Progreso */}
                    <div style={styles.progresoContainer}>
                      <div style={styles.progresoBar}>
                        <div
                          style={{
                            ...styles.progresoFill,
                            width: `${subcontratista.porcentaje_pagado}%`
                          }}
                        ></div>
                      </div>
                    </div>

                    {/* Historial de Pagos */}
                    {subcontratista.pagos && subcontratista.pagos.length > 0 && (
                      <div style={styles.historialPagos}>
                        <strong style={styles.historialTitle}>Historial de Pagos:</strong>
                        {subcontratista.pagos.map((pago, index) => (
                          <div key={pago.id} style={styles.pagoItem}>
                            <span>{pago.concepto}</span>
                            <span style={{fontWeight: '600'}}>{formatMoney(pago.monto)}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    <div style={styles.cardActions}>
                      <button
                        onClick={() => editarSubcontratista(subcontratista)}
                        style={styles.btnEdit}
                      >
                        ✏️ Editar
                      </button>
                      <button
                        onClick={() => abrirFormularioPago(subcontratista)}
                        style={styles.btnPagar}
                        disabled={subcontratista.saldo_pendiente <= 0}
                      >
                        💰 Registrar Pago
                      </button>
                      <button
                        onClick={() => cambiarEstado(subcontratista)}
                        style={styles.btnCambiarEstado}
                      >
                        ✅ Completar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Completados */}
          {subcontratistasCompletados.length > 0 && (
            <>
              <h3 style={{...styles.sectionTitle, marginTop: '30px'}}>
                ✅ Completados ({subcontratistasCompletados.length})
              </h3>
              <div style={styles.listaSubcontratistas}>
                {subcontratistasCompletados.map((subcontratista) => (
                  <div key={subcontratista.id} style={{...styles.subcontratistaCard, opacity: 0.7}}>
                    <div style={styles.cardHeader}>
                      <div>
                        <h4 style={styles.nombre}>{subcontratista.nombre}</h4>
                        <p style={styles.tipo}>{getTipoTrabajoLabel(subcontratista.tipo_trabajo)}</p>
                      </div>
                      <div style={styles.monto}>{formatMoney(subcontratista.monto_contrato)}</div>
                    </div>
                    
                    <div style={styles.cardInfo}>
                      <div style={styles.infoItem}>
                        <span style={styles.infoLabel}>✅ Total Pagado:</span>
                        <span style={{fontWeight: '600', color: '#10b981'}}>
                          {formatMoney(subcontratista.total_pagado)}
                        </span>
                      </div>
                    </div>

                    <div style={styles.cardActions}>
                      <button
                        onClick={() => cambiarEstado(subcontratista)}
                        style={styles.btnCambiarEstado}
                      >
                        🔄 Reactivar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: '20px'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '20px',
    flexWrap: 'wrap',
    gap: '15px'
  },
  title: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1e293b',
    margin: '0 0 5px 0'
  },
  subtitle: {
    fontSize: '14px',
    color: '#64748b',
    margin: 0
  },
  btnPrimary: {
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)'
  },
  resumen: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '15px',
    marginBottom: '20px'
  },
  resumenCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    background: 'white',
    padding: '20px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    border: '1px solid #e2e8f0'
  },
  resumenIcon: {
    fontSize: '36px'
  },
  resumenLabel: {
    fontSize: '13px',
    color: '#64748b',
    margin: '0 0 5px 0',
    fontWeight: '600'
  },
  resumenValue: {
    fontSize: '22px',
    fontWeight: '700',
    color: '#1e293b',
    margin: 0
  },
  formulario: {
    background: 'white',
    padding: '25px',
    borderRadius: '12px',
    marginBottom: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    border: '1px solid #e2e8f0'
  },
  formTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '20px'
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '15px',
    marginBottom: '20px'
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column'
  },
  label: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#475569',
    marginBottom: '6px'
  },
  input: {
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #cbd5e1',
    borderRadius: '8px',
    outline: 'none'
  },
  select: {
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #cbd5e1',
    borderRadius: '8px',
    background: 'white',
    cursor: 'pointer'
  },
  formActions: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'flex-end'
  },
  btnSecondary: {
    padding: '10px 20px',
    background: '#f1f5f9',
    color: '#64748b',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  btnSubmit: {
    padding: '10px 20px',
    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    padding: '20px'
  },
  formularioPago: {
    background: 'white',
    padding: '30px',
    borderRadius: '12px',
    maxWidth: '600px',
    width: '100%',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
  },
  subcontratistaNombre: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '15px'
  },
  infoPago: {
    background: '#f8fafc',
    padding: '15px',
    borderRadius: '8px',
    marginBottom: '20px'
  },
  infoItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '13px',
    color: '#475569',
    padding: '5px 0'
  },
  infoLabel: {
    fontWeight: '600',
    marginRight: '5px'
  },
  loading: {
    textAlign: 'center',
    padding: '40px'
  },
  spinner: {
    border: '4px solid #f3f4f6',
    borderTop: '4px solid #f59e0b',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    animation: 'spin 0.8s linear infinite',
    margin: '0 auto 15px'
  },
  empty: {
    textAlign: 'center',
    padding: '60px 20px',
    background: 'white',
    borderRadius: '12px',
    border: '2px dashed #cbd5e1'
  },
  emptyIcon: {
    fontSize: '64px',
    marginBottom: '15px'
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '15px'
  },
  listaSubcontratistas: {
    display: 'grid',
    gap: '15px',
    marginBottom: '20px'
  },
  subcontratistaCard: {
    background: 'white',
    padding: '20px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    border: '1px solid #e2e8f0'
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '15px',
    gap: '15px'
  },
  nombre: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1e293b',
    margin: '0 0 4px 0'
  },
  tipo: {
    fontSize: '13px',
    color: '#64748b',
    margin: '0 0 8px 0'
  },
  descripcion: {
    fontSize: '13px',
    color: '#475569',
    margin: 0,
    fontStyle: 'italic'
  },
  monto: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#f59e0b'
  },
  cardInfo: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '10px',
    marginBottom: '15px'
  },
  progresoContainer: {
    marginBottom: '15px'
  },
  progresoBar: {
    height: '8px',
    background: '#e5e7eb',
    borderRadius: '999px',
    overflow: 'hidden'
  },
  progresoFill: {
    height: '100%',
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    borderRadius: '999px',
    transition: 'width 0.3s'
  },
  historialPagos: {
    background: '#f8fafc',
    padding: '15px',
    borderRadius: '8px',
    marginBottom: '15px'
  },
  historialTitle: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#475569',
    display: 'block',
    marginBottom: '10px'
  },
  pagoItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '13px',
    color: '#475569',
    padding: '5px 0',
    borderBottom: '1px solid #e2e8f0'
  },
  cardActions: {
    display: 'flex',
    gap: '10px',
    paddingTop: '15px',
    borderTop: '1px solid #e2e8f0'
  },
  btnEdit: {
    flex: 1,
    padding: '8px 16px',
    background: '#f0f9ff',
    color: '#0369a1',
    border: '1px solid #bae6fd',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  btnPagar: {
    flex: 1,
    padding: '8px 16px',
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  btnCambiarEstado: {
    flex: 1,
    padding: '8px 16px',
    background: '#fef3c7',
    color: '#d97706',
    border: '1px solid #fde68a',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer'
  }
};

export default GestionSubcontratistas;